import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderPlus, faFileMedical, faFileCirclePlus, faTimes, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { useKnowledgeBase } from './hooks/useKnowledgeBase';
import FolderTree from './components/FolderTree';
import ArticleViewer from './components/ArticleViewer';
import ArticleEditor from './components/ArticleEditor';
import VersionHistory from './components/VersionHistory';
import FolderDialog from './components/FolderDialog';
import ArticleDialog from './components/ArticleDialog';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';
import { KbFolder, KbArticleStub } from './types';
import {
  Container,
  Sidebar,
  SidebarHeader,
  SidebarActions,
  SidebarSearch,
  ResizeHandle,
  ContentWrapper,
  TabBar,
  Tab,
  TabClose,
  ContentArea,
  IconBtn,
  EmptyState,
} from './styles';

const filterFolders = (folders: KbFolder[], query: string): KbFolder[] => {
  const lower = query.toLowerCase();
  return folders.reduce<KbFolder[]>((acc, folder) => {
    const filteredChildren = filterFolders(folder.children, query);
    const filteredArticles = folder.articles.filter((a) => a.title.toLowerCase().includes(lower));
    const folderMatches = folder.name.toLowerCase().includes(lower);
    if (folderMatches || filteredChildren.length > 0 || filteredArticles.length > 0) {
      acc.push({
        ...folder,
        children: filteredChildren,
        articles: folderMatches ? folder.articles : filteredArticles,
      });
    }
    return acc;
  }, []);
};

const filterArticles = (articles: KbArticleStub[], query: string): KbArticleStub[] => {
  const lower = query.toLowerCase();
  return articles.filter((a) => a.title.toLowerCase().includes(lower));
};

interface KnowledgeBaseProps {
  articleId?: number;
}

const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({ articleId }) => {
  const kb = useKnowledgeBase();
  const [searchQuery, setSearchQuery] = useState('');
  const lastOpenedArticleId = useRef<number | undefined>(undefined);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const resizing = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizing.current) return;
      const newWidth = Math.max(180, Math.min(600, startWidth + ev.clientX - startX));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      resizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth]);

  useEffect(() => {
    if (articleId && !kb.loading && articleId !== lastOpenedArticleId.current) {
      lastOpenedArticleId.current = articleId;
      kb.selectArticle(articleId);
    }
  }, [articleId, kb.loading]);

  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return kb.tree;
    return {
      folders: filterFolders(kb.tree.folders, searchQuery.trim()),
      articles: filterArticles(kb.tree.articles, searchQuery.trim()),
    };
  }, [kb.tree, searchQuery]);

  // Folder dialog state
  const [folderDialog, setFolderDialog] = useState<{
    open: boolean;
    title: string;
    initialValue: string;
    parentId: number | null;
    folderId: number | null;
  }>({ open: false, title: '', initialValue: '', parentId: null, folderId: null });

  // Article dialog state
  const [articleDialog, setArticleDialog] = useState<{
    open: boolean;
    folderId: number | null;
  }>({ open: false, folderId: null });

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const handleCreateFolder = useCallback((parentId: number | null) => {
    setFolderDialog({
      open: true,
      title: parentId !== null ? 'New Subfolder' : 'New Folder',
      initialValue: '',
      parentId,
      folderId: null,
    });
  }, []);

  const handleRenameFolder = useCallback((id: number, currentName: string) => {
    setFolderDialog({
      open: true,
      title: 'Rename Folder',
      initialValue: currentName,
      parentId: null,
      folderId: id,
    });
  }, []);

  const handleFolderDialogConfirm = useCallback(async (value: string, parentId: number | null) => {
    if (folderDialog.folderId !== null) {
      await kb.renameFolder(folderDialog.folderId, value);
    } else {
      await kb.createFolder(value, parentId);
    }
    setFolderDialog((prev) => ({ ...prev, open: false }));
  }, [folderDialog, kb]);

  const handleDeleteFolder = useCallback((id: number) => {
    setDeleteDialog({
      open: true,
      title: 'Delete Folder',
      message: 'Are you sure? The folder must be empty to be deleted.',
      onConfirm: async () => {
        await kb.deleteFolder(id);
        setDeleteDialog((prev) => ({ ...prev, open: false }));
      },
    });
  }, [kb]);

  const handleCreateArticle = useCallback((folderId: number | null) => {
    setArticleDialog({ open: true, folderId });
  }, []);

  const handleArticleDialogConfirm = useCallback(async (title: string, folderId: number | null) => {
    await kb.createArticle(title, folderId);
    setArticleDialog({ open: false, folderId: null });
  }, [kb]);

  const handleDeleteArticle = useCallback((id: number) => {
    setDeleteDialog({
      open: true,
      title: 'Delete Article',
      message: 'Are you sure you want to delete this article? All versions will be lost.',
      onConfirm: async () => {
        await kb.deleteArticle(id);
        setDeleteDialog((prev) => ({ ...prev, open: false }));
      },
    });
  }, [kb]);

  const handleSave = useCallback(async (title: string, content: string) => {
    if (kb.article) {
      await kb.updateArticle(kb.article.id, title, content);
    }
  }, [kb]);

  const renderContent = () => {
    if (kb.editing && kb.article) {
      return (
        <ArticleEditor
          article={kb.article}
          folders={kb.tree.folders}
          onSave={handleSave}
          onCancel={() => kb.setEditing(false)}
        />
      );
    }

    if (kb.article && kb.showVersions) {
      return (
        <VersionHistory
          article={kb.article}
          versions={kb.versions}
          onClose={() => kb.setShowVersions(false)}
          onFetchVersionContent={kb.fetchVersionContent}
          onRestore={kb.restoreVersion}
        />
      );
    }

    if (kb.article) {
      return (
        <ArticleViewer
          article={kb.article}
          folders={kb.tree.folders}
          onEdit={() => kb.setEditing(true)}
          onShowVersions={() => kb.fetchVersions(kb.article!.id)}
          onDelete={() => handleDeleteArticle(kb.article!.id)}
        />
      );
    }

    if (kb.articleLoading) {
      return <ContentArea><EmptyState>Loading...</EmptyState></ContentArea>;
    }

    return (
      <ContentArea>
        <EmptyState>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>
            <FontAwesomeIcon icon={faFileCirclePlus} />
          </div>
          <div>Select an article or create a new one</div>
        </EmptyState>
      </ContentArea>
    );
  };

  return (
    <Container>
      <Sidebar style={{ width: sidebarWidth }}>
        <ResizeHandle onMouseDown={handleResizeStart} />
        <SidebarHeader>
          <h3>Knowledge Base</h3>
          <SidebarActions>
            <IconBtn title="Refresh" onClick={() => kb.fetchTree()}>
              <FontAwesomeIcon icon={faSyncAlt} />
            </IconBtn>
            <IconBtn title="New folder" onClick={() => handleCreateFolder(null)}>
              <FontAwesomeIcon icon={faFolderPlus} />
            </IconBtn>
            <IconBtn title="New article" onClick={() => handleCreateArticle(null)}>
              <FontAwesomeIcon icon={faFileMedical} />
            </IconBtn>
          </SidebarActions>
        </SidebarHeader>
        <SidebarSearch
          placeholder="Filter..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {kb.loading ? (
          <EmptyState>Loading...</EmptyState>
        ) : (
          <FolderTree
            folders={filteredTree.folders}
            articles={filteredTree.articles}
            selectedNode={kb.selectedNode}
            onSelectArticle={kb.selectArticle}
            onSelectFolder={kb.selectFolder}
            onCreateFolder={handleCreateFolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            onCreateArticle={handleCreateArticle}
            onDeleteArticle={handleDeleteArticle}
            onMoveArticle={kb.moveArticle}
            onMoveFolder={kb.moveFolder}
          />
        )}
      </Sidebar>

      <ContentWrapper>
        {kb.openTabs.length > 0 && (
          <TabBar>
            {kb.openTabs.map((tab) => (
              <Tab
                key={tab.id}
                $active={tab.id === kb.activeTabId}
                onClick={() => kb.switchTab(tab.id)}
              >
                {tab.title}
                <TabClose
                  onClick={(e) => {
                    e.stopPropagation();
                    kb.closeTab(tab.id);
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </TabClose>
              </Tab>
            ))}
          </TabBar>
        )}
        {renderContent()}
      </ContentWrapper>

      <FolderDialog
        open={folderDialog.open}
        title={folderDialog.title}
        initialValue={folderDialog.initialValue}
        initialParentId={folderDialog.parentId}
        folders={kb.tree.folders}
        showFolderSelect={folderDialog.folderId === null}
        onConfirm={handleFolderDialogConfirm}
        onCancel={() => setFolderDialog((prev) => ({ ...prev, open: false }))}
      />

      <ArticleDialog
        open={articleDialog.open}
        initialFolderId={articleDialog.folderId}
        folders={kb.tree.folders}
        onConfirm={handleArticleDialogConfirm}
        onCancel={() => setArticleDialog({ open: false, folderId: null })}
      />

      <DeleteConfirmDialog
        open={deleteDialog.open}
        title={deleteDialog.title}
        message={deleteDialog.message}
        onConfirm={deleteDialog.onConfirm}
        onCancel={() => setDeleteDialog((prev) => ({ ...prev, open: false }))}
      />
    </Container>
  );
};

export default KnowledgeBase;
